import { IntlShape } from 'react-intl'
import { lazy, object, string } from 'yup'

import { StepType, TriggerTypes } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import { getValidationSchema as getAdaptiveCardValidationSchema } from './AdaptiveCard/AdaptiveCardOperation'
import { getValidationSchema as getAPIRequestValidationSchema } from './APIRequestOperation'
import { getValidationSchema as getBooleanValidationSchema } from './Boolean/BooleanOperation'
import { getValidationSchema as getCustomCodeValidationSchema } from './CustomCodeOperation'
import { getValidationSchema as getDataSourceValidationSchema } from './DataSourceOperation'
import { getValidationSchema as getDataStorageValidationSchema } from './DataStorageOperation'
import { getValidationSchema as getDataTransformValidationSchema } from './DataTransform/DataTransformOperation'
import { getValidationSchema as getDeployFeatureValidationSchema } from './DeployFeatureOperation'
import { getValidationSchema as getDocumentSearchStorageValidationSchema } from './DocumentSearchStorageOperation'
import { getValidationSchema as getDocumentTextSplitterValidationSchema } from './DocumentTextSplitterOperation'
import { getValidationSchema as getFileDownloadValidationSchema } from './FileDownloadOperation'
import { getValidationSchema as getFileUploadValidationSchema } from './FileUploadOperation'
import { getValidationSchema as getGenerateMicroappTokenValidationSchema } from './GenerateMicroappToken'
import { getValidationSchema as getInvokeLambdaValidationSchema } from './InvokeLambdaOperation'
import { getValidationSchema as getKnnSearchValidationSchema } from './KnnSearchOperation'
import { getValidationSchema as getLogValidationSchema } from './LogOperation'
import { getValidationSchema as getLoopValidationSchema } from './LoopOperation'
import messages from './messages'
import { getValidationSchema as getNotificationDateFormatterValidationSchema } from './Notification/DateFormatterOperation'
import { getValidationSchema as getNotificationDeleteValidationSchema } from './Notification/DeleteOperation'
import { getValidationSchema as getNotificationValidationSchema } from './Notification/NotificationOperation'
import { getValidationSchema as getNotificationViewValidationSchema } from './NotificationView'
import { getValidationSchema as getRagPromptValidationSchema } from './RagPromptOperation'
import { getValidationSchema as getResponseValidationSchema } from './ResponseOperation'
import { getValidationSchema as getRunFeatureValidationSchema } from './RunFeatureOperation'
import { getValidationSchema as getSetConfigVariableValidationSchema } from './SetConfigVariableOperation'
import { getValidationSchema as getTextToEmbeddingsValidationSchema } from './TextToEmbeddingsOperation'
import { getValidationSchema as getAppUITriggerValidationSchema } from './Triggers/AppUITrigger/AppUITrigger'
import { getValidationSchema as getFeatureTriggerValidationSchema } from './Triggers/FeatureTrigger/FeatureTrigger'
import { getValidationSchema as getHttpTriggerValidationSchema } from './Triggers/HttpRequestTrigger/HttpRequestTrigger'
import { getValidationSchema as getScheduledTriggerValidationSchema } from './Triggers/ScheduledTrigger/ScheduledTrigger'
import { getValidationSchema as getDataSourceTriggerValidationSchema } from './Triggers/WorkgridDataSourceEventTrigger/WorkgridDataSourceEventTrigger'
import { getValidationSchema as getUserLookupValidationSchema } from './UserLookupOperation'
import { getValidationSchema as getWorkgridDataSourceValidationSchema } from './WorkgridDataSourceOperation'

const validationSchemaPerTrigger = () =>
  new Map<TriggerTypes, (intl: IntlShape, formData: any, isDevelopmentFeature: boolean) => any>([
    [TriggerTypes.scheduled, getScheduledTriggerValidationSchema],
    [TriggerTypes.dataSourceEvent, getDataSourceTriggerValidationSchema],
    [TriggerTypes.http, getHttpTriggerValidationSchema],
    [TriggerTypes.microappView, getAppUITriggerValidationSchema],
    [TriggerTypes.featureTrigger, getFeatureTriggerValidationSchema]
  ])

// We have to make this a function that returns the map to prevent it loading the schema before formik is ready
const getValidationSchemaMap = () =>
  new Map<StepType, (intl: IntlShape, formData: any, inputSchema: any, agentsFlagOn: boolean) => any>([
    [StepType.Log, getLogValidationSchema],
    [StepType.Boolean, getBooleanValidationSchema],
    [StepType.Loop, getLoopValidationSchema],
    [StepType.CustomCode, getCustomCodeValidationSchema],
    [StepType.Notification, getNotificationValidationSchema],
    [StepType.NotificationView, getNotificationViewValidationSchema],
    [StepType.NotificationDateFormatter, getNotificationDateFormatterValidationSchema],
    [StepType.NotificationDelete, getNotificationDeleteValidationSchema],
    [StepType.DataSource, getDataSourceValidationSchema],
    [StepType.APIRequest, getAPIRequestValidationSchema],
    [StepType.DataStorage, getDataStorageValidationSchema],
    [StepType.DataTransform, getDataTransformValidationSchema],
    [StepType.Response, getResponseValidationSchema],
    [StepType.RunFeature, getRunFeatureValidationSchema],
    [StepType.DeployFeature, getDeployFeatureValidationSchema],
    [StepType.UserLookup, getUserLookupValidationSchema],
    [StepType.FileUpload, getFileUploadValidationSchema],
    [StepType.FileDownload, getFileDownloadValidationSchema],
    [StepType.DocumentTextSplitter, getDocumentTextSplitterValidationSchema],
    [StepType.TextToEmbeddings, getTextToEmbeddingsValidationSchema],
    [StepType.KnnSearch, getKnnSearchValidationSchema],
    [StepType.RagPrompt, getRagPromptValidationSchema],
    [StepType.AdaptiveCard, getAdaptiveCardValidationSchema],
    [StepType.SetConfigVariable, getSetConfigVariableValidationSchema],
    [StepType.DocumentSearchStorage, getDocumentSearchStorageValidationSchema],
    [StepType.WorkgridDataSource, getWorkgridDataSourceValidationSchema],
    [StepType.InvokeLambda, getInvokeLambdaValidationSchema],
    [StepType.GenerateMicroappToken, getGenerateMicroappTokenValidationSchema]
  ])

/**
 * Returns a validation schema based on StepType or TriggerType
 * @param {object} args
 * @param {object} [args.intl] - The intl object for formatting error messages, if not provided error messages will be empty
 * @param {string} args.type - The string from StepType or TriggerTypes enum
 */
const getValidationSchemaByType = ({
  intl = { formatMessage: () => '' },
  type,
  inputSchema,
  agentsFlagOn = false
}: {
  intl?: { formatMessage: (T: any) => string }
  type: StepType | TriggerTypes
  inputSchema?: any
  agentsFlagOn?: boolean
}) => {
  const validationObj: any = {
    name: string().required(intl.formatMessage(messages.operationNameRequired))
  }

  if (validationSchemaPerTrigger().get(type as TriggerTypes)) {
    validationObj.triggerData = lazy((formObject: any) => {
      return object().shape(
        // @ts-expect-error figure it out TS
        validationSchemaPerTrigger().get(type)(intl, formObject)
      )
    })
  } else if (getValidationSchemaMap().get(type as StepType)) {
    // Step data validation belongs in the stepData object
    validationObj.stepData = lazy((formObject: any) => {
      // @ts-expect-error
      return object().shape(getValidationSchemaMap().get(type)(intl, formObject, inputSchema, agentsFlagOn))
    })
  }
  return object().shape(validationObj)
}

export default getValidationSchemaByType
